import { useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useLot } from '../hooks/useLot.js'
import { useLots } from '../hooks/useLots.js'
import ImageGallery from '../components/ImageGallery.jsx'
import Loader from '../components/Loader.jsx'
import ErrorState from '../components/ErrorState.jsx'
import Countdown from '../components/Countdown.jsx'
import SourceBadge from '../components/SourceBadge.jsx'
import LotCard from '../components/LotCard.jsx'
import { formatPrice, formatDate, countryName } from '../utils/format.js'
import { STATUS_LABELS } from '../data/constants.js'
import { originalUrl } from '../utils/originalUrl.js'

export default function LotDetailPage() {
  const { id } = useParams()
  const { lot, loading, error } = useLot(id)

  // Para lotes terminados: mostrar "Ähnliche aktive Lose" de la misma subasta
  const isEnded = lot && (lot.status === 'ended' || lot.status === 'sold' || lot.status === 'cancelled' || lot.status === 'reserve_not_met')
  const similarParams = useMemo(() => (
    isEnded && lot?.auction_id
      ? { status: 'active', auction_id: lot.auction_id, per_page: 4, order: 'closing' }
      : { status: 'active', per_page: 4, order: 'closing' }
  ), [isEnded, lot?.auction_id])
  const similar = useLots(isEnded ? similarParams : null)

  useEffect(() => {
    if (lot) {
      document.title = `${lot.title} | Zuchlag`
      document.querySelector('link[rel="canonical"]')?.setAttribute('href', `https://zuchlag.de/lote/${lot.id}`)

      const productLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: lot.title,
        image: lot.main_image_url || (lot.images?.[0]?.image_url),
        description: lot.description || undefined,
        brand: { '@type': 'Brand', name: lot.source?.name || undefined },
        offers: {
          '@type': 'Offer',
          price: lot.current_bid ?? lot.final_bid ?? lot.start_bid,
          priceCurrency: lot.currency || 'EUR',
          availability: lot.closes_at && new Date(lot.closes_at) > new Date() ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
          url: lot.original_url || undefined
        }
      }
      let script = document.getElementById('jsonld-product')
      if (!script) {
        script = document.createElement('script')
        script.type = 'application/ld+json'
        script.id = 'jsonld-product'
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify(productLd)
    } else {
      document.title = 'Auktion | Zuchlag'
    }
  }, [lot])

  if (loading) return <div className="container"><Loader /></div>
  if (error) return <div className="container"><ErrorState message={error} /></div>
  if (!lot) return <div className="container"><ErrorState message="Lote nicht gefunden." /></div>

  const statusLabel = STATUS_LABELS[lot.status] || lot.status
  const images = lot.images || []
  const origUrl = originalUrl(lot)
  const hasBids = (lot.bids_count || 0) > 0

  // Precio a mostrar según estado
  const priceLabel = isEnded ? 'Letztes Gebot' : (hasBids ? 'Aktuelles Gebot' : 'Startgebot')
  const priceValue = isEnded ? (lot.final_bid ?? lot.current_bid) : (lot.current_bid ?? lot.start_bid)

  return (
    <div className="page-detail container">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Start</Link> / <Link to="/suche">Auktionen</Link> / <span>{lot.title}</span>
      </nav>

      <div className="detail-grid">
        <div className="detail-media">
          <ImageGallery images={images} title={lot.title} />
        </div>

        <div className="detail-info">
          <div className="detail-tags">
            <SourceBadge source={lot.source} />
            {lot.status && <span className={`status-tag status-${String(lot.status).toLowerCase()}`}>{statusLabel}</span>}
          </div>

          <h1>{lot.title}</h1>

          {lot.condition && lot.condition !== 'NOT_CHECKED' && (
            <p className="detail-cond">Zustand: {lot.condition.replace(/_/g, ' ')}</p>
          )}

          {lot.description && <p className="detail-desc">{lot.description}</p>}

          <dl className="detail-specs">
            <div className="spec-row">
              <dt>{priceLabel}</dt>
              <dd className="spec-price">{priceValue != null ? formatPrice(priceValue, lot.currency) : '–'}</dd>
            </div>
            {lot.bids_count != null && (
              <div className="spec-row"><dt>Anzahl Gebote</dt><dd>{lot.bids_count}</dd></div>
            )}
            {lot.start_bid != null && !isEnded && (
              <div className="spec-row"><dt>Startgebot</dt><dd>{formatPrice(lot.start_bid, lot.currency)}</dd></div>
            )}
            {lot.reserve_status && (
              <div className="spec-row"><dt>Mindestpreis</dt><dd>{lot.reserve_status === 'met' ? 'erreicht' : 'nicht erreicht'}</dd></div>
            )}
            {lot.starts_at && (
              <div className="spec-row"><dt>Beginnt am</dt><dd>{formatDate(lot.starts_at)}</dd></div>
            )}
            {lot.closes_at && (
              <div className="spec-row"><dt>Endet am</dt><dd>{formatDate(lot.closes_at)} (Europe/Berlin)</dd></div>
            )}
            {lot.closes_at && !isEnded && lot.status !== 'upcoming' && (
              <div className="spec-row"><dt>Countdown</dt><dd><Countdown closesAt={lot.closes_at} /></dd></div>
            )}
            {lot.city && lot.city !== 'Multiple locations' && (
              <div className="spec-row"><dt>Ort</dt><dd>{lot.city}</dd></div>
            )}
            {lot.country_code && (
              <div className="spec-row"><dt>Land</dt><dd>{countryName(lot.country_code)}</dd></div>
            )}
            {lot.seller && (
              <div className="spec-row"><dt>Verkäufer</dt><dd>{lot.seller}</dd></div>
            )}
            {lot.quantity && (
              <div className="spec-row"><dt>Anzahl/Menge</dt><dd>{lot.quantity}</dd></div>
            )}
            {lot.source?.name && (
              <div className="spec-row"><dt>Auktionshaus</dt><dd>{lot.source.name}</dd></div>
            )}
            {lot.auction_lot_number && (
              <div className="spec-row"><dt>Losnummer</dt><dd>{lot.auction_lot_number}</dd></div>
            )}
            {lot.external_id && (
              <div className="spec-row"><dt>Los-ID</dt><dd>{lot.external_id}</dd></div>
            )}
            {lot.last_synced_at && (
              <div className="spec-row"><dt>Stand der Daten</dt><dd>{formatDate(lot.last_synced_at)}</dd></div>
            )}
          </dl>

          {origUrl && (
            <a
              className={`btn btn-primary btn-lg btn-block ${isEnded ? 'btn-view' : ''}`}
              href={origUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {isEnded ? 'Originalangebot ansehen ↗' : 'Zum Originalangebot ↗'}
            </a>
          )}

          <p className="detail-disclaimer">
            Zuchlag ist ein Such- und Vergleichsportal. Gebote und Käufe erfolgen ausschließlich auf
            der Plattform des jeweiligen Auktionshauses.
          </p>
          <p className="detail-disclaimer">
            Alle Preise, Gebühren, Steuern und Auktionsbedingungen werden auf der Website des
            jeweiligen Auktionshauses angezeigt. Maßgeblich sind ausschließlich die Angaben des
            Auktionshauses.
          </p>
        </div>
      </div>

      {/* Atributos técnicos / especificaciones */}
      {lot.attributes && lot.attributes.length > 0 && (
        <section className="section">
          <h2>Technische Details</h2>
          <dl className="attr-grid">
            {lot.attributes.map((a, i) => (
              <div key={i} className="attr-item">
                <dt>{a.name}</dt>
                <dd>{a.value}{a.unit ? ` ${a.unit}` : ''}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* Para lotes terminados: lotes activos similares */}
      {isEnded && (
        <section className="section">
          <div className="section-head">
            <h2>Ähnliche aktive Lose</h2>
          </div>
          {similar.loading && <Loader />}
          {!similar.loading && similar.data.length > 0 && (
            <div className="lot-grid">
              {similar.data.filter((s) => s.id !== lot.id).map((s) => <LotCard key={s.id} lot={s} />)}
            </div>
          )}
          {!similar.loading && similar.data.length === 0 && (
            <p className="muted">Derzeit keine ähnlichen aktiven Lose.</p>
          )}
        </section>
      )}

      <div className="detail-back">
        <Link to="/suche" className="btn btn-outline">← Zurück zur Suche</Link>
      </div>
    </div>
  )
}
